import { useEffect, useState } from 'react';
import '../App.scss';
import { Button } from '@mui/base/Button';
import { useNavigate } from 'react-router-dom';

import {
  ProjectWithResponse,
  ProjectStatus,
  ProjectDetailModel,
  AdminClient,
} from '../types';
import { createClient } from '../api/clients';
import { APIPATH } from '../App';

function ProjectsAdminPage() {
  const navigate = useNavigate();

  const [adminProjects, setAdminProjects] = useState<ProjectDetailModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const adminClient = createClient(AdminClient);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const result = await adminClient.getProjects();
      setAdminProjects(result);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/project');
  };

  const handleEdit = (id?: string) => {
    if (!id) return;
    navigate(`/project/?p=${id}`);
  };

  const handleReport = (id?: string) => {
    if (!id) return;
    navigate(`/report/?r=${id}`);
  };

  const handleGoToTeamManagement = () => navigate('/team-management');
  const handleGoToAdmin = () => navigate('/admin');

  const allProjectsEmpty =
    adminProjects.length === 0 ||
    adminProjects.every(
      (company) => !company.projects || company.projects.length === 0
    );

  return (
    <div className="main container py-4 mb-6">
      <div
        className="pb-4 mb-4"
        style={{ width: '100%', maxWidth: '700px', margin: '0 auto' }}
      >
        <div className="d-flex gap-2" style={{ marginBottom: "3em" }}>
          <Button className="styled-button" onClick={handleGoToAdmin} id="btn-admin">
            Admin
          </Button>    
          <Button className="styled-button" onClick={handleCreate} id="btn-create-project">
              Create project
          </Button>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">Project Admin</h2>
        </div>


        {loading && <p>Loading projects…</p>}

        {errorMessage && !loading && (
          <div className="form-error mb-3">{errorMessage}</div>
        )}

        {!loading && !errorMessage && (
          <>
            {allProjectsEmpty ? (
              <p>No projects found.</p>
            ) : (
              <div style={{ width: '100%' }}>
                {adminProjects.map((company, index) => {
                  const companyProjects = company.projects ?? [];
                  return (
                    <div key={company.companyName ?? index} className="mb-5">
                      <h3 className="mb-3">
                        {company.companyName} ({company.projectsLeft} projects left)
                      </h3>

                      <table className="table" style={{ width: '100%' }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left' }}>Project name</th>
                            <th style={{ textAlign: 'left' }}></th>
                            <th style={{ textAlign: 'right' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {companyProjects.map((project: ProjectWithResponse) => (
                            <tr key={project._id}>
                              <td>{project.projectTitle ?? '(Untitled project)'}</td>
                              <td>
                                 {project.status ===
                                  ProjectStatus.AwaitingAdminApproval ? (
                                    <span>Awaiting Response</span>
                                ): (<span></span>)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                {project.status ===
                                ProjectStatus.AwaitingAdminApproval ? (
                                  <Button
                                    className="styled-button"
                                    onClick={() =>
                                      handleReport(project.response?.[0]?._id)
                                    }
                                  >
                                    Respond
                                  </Button>
                                ) : project.status === ProjectStatus.Complete ? (
                                  <Button
                                    className="styled-button"
                                    onClick={() =>
                                      handleReport(project.response?.[0]?._id)
                                    }
                                  >
                                    View report
                                  </Button>
                                ) : (
                                  <Button
                                    className="styled-button"
                                    onClick={() => handleEdit(project._id)}
                                  >
                                    Edit
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ProjectsAdminPage;
